import React, { useCallback, useEffect, useState } from 'react';
import { useMappedState, useDispatch } from 'redux-react-hook';
import { Trans } from '@lingui/macro';
import { navigate } from '@reach/router';
import { linkTo } from 'utils/links';
import Config from 'config';

// Redux
import { removeMessage } from 'store/modules/app/actions';
import { toggleSearch } from 'store/modules/search/actions';
import { getMessages, getSelectedLanguage, getConfig } from 'store/modules/app/selectors';
import { getCurrentUser, isAuthenticated } from 'store/modules/auth/selectors';
import { getPlayingSong } from 'store/modules/player/selectors';

// Components
import Banner from 'components/Panel/Banner';
import Button from 'components/Button';
import SideNav from 'components/SideNav';
import TopNav from 'containers/TopNav';
import Search from 'containers/Search';
import Toast from 'components/Toast';
import CloseIcon from 'components/Icon/CheckboxCheck';
import './styles.css';

const forceLogin = Config.app.APP_FORCE_LOGIN === 'Enabled';

const mapState = state => ({
  config: getConfig(state),
  language: getSelectedLanguage(state),
  isLoggedIn: isAuthenticated(state),
  current: getCurrentUser(state),
  song: getPlayingSong(state),
  messages: getMessages(state),
});

export default function Main(props) {
  const { children, isPublic, path, onlyAdmins, onlyAuthors } = props;
  const { config, current, song, messages, isLoggedIn } = useMappedState(mapState);
  const dispatch = useDispatch();
  const onCloseToast = useCallback(() => dispatch(removeMessage(messages[0])), [messages]);
  const onToggleSearch = useCallback(() => dispatch(toggleSearch()), []);
  const message = messages[0];
  useEffect(() => {
    if (forceLogin && !isLoggedIn) {
      navigate(linkTo('/auth/sign-in'));
    } else if (!isPublic && !isLoggedIn) {
      navigate(linkTo('/auth/sign-in'));
    } else if (current.id && onlyAdmins && !current.admin) {
      navigate(linkTo('/discover'));
    } else if (current.id && onlyAuthors && !current.author) {
      navigate(linkTo('/discover'));
    }
  }, [path, isPublic, isLoggedIn]);

  if (forceLogin && !isLoggedIn || !isPublic && !isLoggedIn || onlyAdmins && !current.admin || onlyAuthors && !current.author) {
    return (
      <div />
    );
  }

  return (
    <div className="lg:flex h-screen w-screen">
      {!config.IS_NATIVE &&
        <SideNav menu={Config.app.APP_MENU} user={current} onToggleSearch={onToggleSearch} />
      }
      <div className="flex flex-col flex-1 h-screen overflow-hidden" style={{ paddingBottom: song ? '70px' : '0' }}>
        <TopNav />
        <main className="flex-1 Layout_Main__main overflow-hidden">
          {forceLogin && current.id && children}
          {!forceLogin && children}
        </main>
      </div>
      {!config.IS_NATIVE &&
        <CookiesBanner />
      }
      { message &&
        <Toast key={message.id} type={message.type} onClose={onCloseToast}>
          {message.message}
        </Toast>
      }
      <Search />
    </div>
  );
}

function CookiesBanner() {
  const [accepted, setAccepted] = useState(true);
  const onClose = () => {
    localStorage.setItem(Config.cookies.policy, true);
    setAccepted(true);
  };
  useEffect(() => {
    const accepted = !!localStorage.getItem(Config.cookies.policy);
    setAccepted(accepted);
  }, []);

  if (accepted) {
    return null;
  };

  const actions = (
    <Button onClick={onClose} type="primary" className="text-default relative top-0">
      <CloseIcon width={25} height={25} className="inline-block" /> <Trans>Accept</Trans>
    </Button>
  );

  return (
    <Banner test="cookies-banner" actions={actions}>
      <Trans>To help personalize content, tailor and measure ads, and provide a safer experience, we use cookies. By clicking or navigating the site, you agree to allow our collection of information on and off {Config.app.APP_TITLE} through cookies. Learn more, including about available controls <a href="/web/legal/cookies" className="underline text-default">Cookies Policy</a></Trans>
    </Banner>
  );
}
